import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import './Flights.scss';
import { Flight } from 'models/flight';
import { Flights as FlightsInterface } from 'models/flights';
import { FlightTableRow } from 'models/flightTableRow';
import { Dashboard } from 'models/dashboard';
import { getFlights } from 'api/flights';

import FlightTable from 'components/FlightTable';
import Loader from 'components/Loader';
import { parseFlightTime } from 'utils/format';

interface FlightsProps {
    dashboard: Dashboard;
    setDashboardLastUpdatedAt: (arg0: Date | null) => void;
    filterAlertedFlights: boolean;
}

const useFlightsQuery = (props: FlightsProps) => {
    return useQuery<Flight[], Error, FlightTableRow[]>(
        ['dashboard', 'flights', props.dashboard.id],
        () =>
            getFlights(props.dashboard.id).then((data: FlightsInterface) => {
                const lastUpdatedAt = parseFlightTime(data.lastUpdated);
                const lastUpdatedAtDate = lastUpdatedAt ? new Date(lastUpdatedAt * 1000) : null;
                props.setDashboardLastUpdatedAt(lastUpdatedAtDate);
                return data.flights;
            }),
        {
            select: (data) => data.map((flight) => new FlightTableRow(flight, props.dashboard)),
            useErrorBoundary: false,
            onError: (error) => {
                toast.error(`Error fetching flight data: ${error.message}`);
            },
            refetchInterval: 1000 * props.dashboard.pollDelay,
            refetchIntervalInBackground: true,
            refetchOnReconnect: true,
            refetchOnMount: 'always',
        }
    );
};

export default function Flights(props: FlightsProps): JSX.Element {
    const { data = [], isLoading } = useFlightsQuery(props);
    const filteredData = data.filter(
        (row) =>
            !props.filterAlertedFlights ||
            row.hasAlert(props.dashboard.alerts.thresholdEarly, props.dashboard.alerts.thresholdLate)
    );

    if (isLoading) return <Loader />;

    return (
        <div className="flights" data-testid="flights">
            <h3 style={{ display: 'none' }}>Flights Table {props.dashboard.id}</h3>
            <span style={{ display: 'none' }}>Name: {props.dashboard.name}</span>
            {filteredData.length > 0 ? (
                <FlightTable rows={filteredData} dashboard={props.dashboard} />
            ) : (
                <div className="flights__no-flights-found">No flights found</div>
            )}
        </div>
    );
}

Flights.propTypes = {
    dashboard: PropTypes.object,
    setDashboardLastUpdatedAt: PropTypes.func,
    filterAlertedFlights: PropTypes.bool,
};
